import React, { useState, useRef } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import { FiMapPin } from 'react-icons/fi';
import PropTypes from 'prop-types';

const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && error.error) return error.error;
  return 'An error occurred';
};

const COLORS = {
  p_count: '#F59E0B',
  j_count: '#3B82F6'
};

const LEGEND = [
  { key: 'p_count', label: 'Number of P', color: COLORS.p_count },
  { key: 'j_count', label: 'Number of J', color: COLORS.j_count }
];

const Tooltip = ({ visible, x, y, item }) => {
  if (!visible || !item) return null;
  return (
    <div
      style={{
        position: 'fixed',
        left: x + 14,
        top: y - 10,
        zIndex: 9999,
        pointerEvents: 'none',
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        padding: '10px 14px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
        minWidth: '160px'
      }}
    >
      <p className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-2 tw-border-b tw-border-gray-100 tw-pb-1">
        {item.unit_name}
      </p>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-1">
        <span className="tw-text-xs tw-text-gray-600 tw-font-semibold">Total CO#</span>
        <span className="tw-text-xs tw-font-bold tw-text-gray-900">{item.total_cos ?? 0}</span>
      </div>
      {LEGEND.map(s => (
        <div key={s.key} className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-1">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-inline-block tw-w-2.5 tw-h-2.5 tw-rounded-sm" style={{ backgroundColor: s.color }} />
            <span className="tw-text-xs tw-text-gray-600">{s.label}</span>
          </div>
          <span className="tw-text-xs tw-font-bold" style={{ color: s.color }}>
            {item[s.key] ?? 0}
          </span>
        </div>
      ))}
      <div className="tw-mt-2 tw-pt-1 tw-border-t tw-border-gray-100 tw-flex tw-justify-between">
        <span className="tw-text-xs tw-text-gray-500">Total Projects</span>
        <span className="tw-text-xs tw-font-bold tw-text-gray-900">{item.total_projects ?? 0}</span>
      </div>
    </div>
  );
};

const unitItemShape = PropTypes.shape({
  unit_id:        PropTypes.number,
  unit_name:      PropTypes.string,
  total_cos:      PropTypes.number,
  total_projects: PropTypes.number,
  p_count:        PropTypes.number,
  j_count:        PropTypes.number
});

Tooltip.propTypes = {
  visible: PropTypes.bool,
  x:       PropTypes.number,
  y:       PropTypes.number,
  item:    unitItemShape
};

const SalesUnitColumnChart = ({ data }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, item: null });
  const svgRef = useRef(null);

  if (!data || data.length === 0) return null;

  const chartData = data.slice(0, 12);

  const height  = 340;
  const pad     = { top: 40, right: 20, bottom: 70, left: 55 };
  const minColW = 60;
  const plotW   = Math.max(chartData.length * minColW, 480);
  const plotH   = height - pad.top - pad.bottom;
  const width   = plotW + pad.left + pad.right;

  const maxVal  = Math.max(...chartData.map(d => d.total_cos || 0));
  const yMax    = maxVal > 0 ? Math.ceil(maxVal * 1.2) : 10;
  const yScale  = plotH / yMax;
  const colW    = plotW / chartData.length;
  const barW    = Math.min(colW * 0.55, 44);
  const yTicks  = 5;

  const handleMouseMove  = (e, item) => setTooltip({ visible: true, x: e.clientX, y: e.clientY, item });
  const handleMouseLeave = () => setTooltip({ visible: false, x: 0, y: 0, item: null });

  return (
    <>
      <Tooltip {...tooltip} />
      <div className="tw-relative">
        <svg
          ref={svgRef}
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="tw-w-full"
        >
          {/* Grid lines + Y labels */}
          {Array.from({ length: yTicks + 1 }).map((_, i) => {
            const val = Math.round((i / yTicks) * yMax);
            const y   = height - pad.bottom - (i / yTicks) * plotH;
            return (
              <React.Fragment key={i}>
                <line x1={pad.left} y1={y} x2={width - pad.right} y2={y}
                  stroke="#e5e7eb" strokeWidth="1" strokeDasharray={i === 0 ? '0' : '4'} />
                <text x={pad.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#9ca3af">
                  {val}
                </text>
              </React.Fragment>
            );
          })}

          {/* Y-axis */}
          <line x1={pad.left} y1={pad.top} x2={pad.left} y2={height - pad.bottom}
            stroke="#d1d5db" strokeWidth="1.5" />

          {/* Stacked bars */}
          {chartData.map((item, gi) => {
            const cx    = pad.left + gi * colW + colW / 2;
            const bx    = cx - barW / 2;
            const base  = height - pad.bottom;

            const pVal  = item.p_count || 0;
            const jVal  = item.j_count || 0;
            const total = item.total_cos || 0;

            const jH    = jVal * yScale;
            const pH    = pVal * yScale;
            const jY    = base - jH - pH;
            const pY    = base - pH;

            return (
              <g
                key={gi}
                onMouseMove={(e) => handleMouseMove(e, item)}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: 'pointer' }}
              >
                {/* Hover zone */}
                <rect x={pad.left + gi * colW} y={pad.top} width={colW} height={plotH} fill="transparent" />

                {/* J segment (top, blue) */}
                {jVal > 0 && (
                  <rect x={bx} y={jY} width={barW} height={Math.max(jH, 2)}
                    fill={COLORS.j_count} rx="3" opacity="0.9" />
                )}
                {/* P segment (bottom, amber) */}
                {pVal > 0 && (
                  <rect x={bx} y={pY} width={barW} height={Math.max(pH, 2)}
                    fill={COLORS.p_count}
                    rx={jVal > 0 ? '0' : '3'}
                    opacity="0.9"
                  />
                )}

                {/* Segment labels inside bars (only if tall enough) */}
                {jH > 16 && (
                  <text x={cx} y={jY + jH / 2 + 4} textAnchor="middle"
                    fontSize="10" fontWeight="700" fill="white" pointerEvents="none">
                    {jVal}
                  </text>
                )}
                {pH > 16 && (
                  <text x={cx} y={pY + pH / 2 + 4} textAnchor="middle"
                    fontSize="10" fontWeight="700" fill="white" pointerEvents="none">
                    {pVal}
                  </text>
                )}

                {/* Total label above bar */}
                {total > 0 && (
                  <text x={cx} y={jY - 5} textAnchor="middle"
                    fontSize="11" fontWeight="700" fill="#374151" pointerEvents="none">
                    {total}
                  </text>
                )}

                {/* X-axis labels */}
                <text x={cx} y={height - pad.bottom + 16} textAnchor="middle"
                  fontSize="11" fontWeight="600" fill="#374151" pointerEvents="none">
                  {(item.unit_name || '').substring(0, 8)}
                </text>
                <text x={cx} y={height - pad.bottom + 30} textAnchor="middle"
                  fontSize="10" fill="#9ca3af" pointerEvents="none">
                  {total} COs
                </text>
              </g>
            );
          })}

          {/* Legend */}
          {LEGEND.map((s, i) => (
            <g key={s.key} transform={`translate(${pad.left + i * 130}, 12)`}>
              <rect x="0" y="0" width="12" height="12" fill={s.color} rx="2" />
              <text x="16" y="10" fontSize="11" fill="#374151" fontWeight="500">{s.label}</text>
            </g>
          ))}
        </svg>
      </div>
    </>
  );
};

SalesUnitColumnChart.propTypes = {
  data: PropTypes.arrayOf(unitItemShape)
};

const SalesUnitChart = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="tw-bg-red-50 tw-border tw-border-red-200 tw-p-6">
        <p className="tw-text-red-600 tw-font-semibold">Error loading sales unit statistics</p>
        <p className="tw-text-red-500 tw-text-sm">{getErrorMessage(error)}</p>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <div className="tw-flex tw-flex-col tw-items-center tw-py-10 tw-text-gray-400">
          <FiMapPin size={32} className="tw-mb-2" />
          <p className="tw-text-sm">No sales unit data available</p>
        </div>
      </Card>
    );
  }

  const totalCOs      = data.reduce((s, d) => s + (d.total_cos || 0), 0);
  const totalProjects = data.reduce((s, d) => s + (d.total_projects || 0), 0);
  const topUnit       = data[0]?.unit_name || '—';

  return (
    <Card className="tw-bg-gradient-to-br tw-from-teal-50 tw-via-white tw-to-cyan-50 tw-border tw-border-gray-200 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
      <div className="tw-p-6 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiMapPin className="tw-text-teal-600" size={20} />
          Change Orders by Sales Unit / Region
        </h3>
        <p className="tw-text-sm tw-text-gray-600 tw-mt-1">
          Distribution of COs and projects across sales regions (hover bars for details)
        </p>
      </div>

      <div className="tw-p-6">
        <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-mb-6">
          <SalesUnitColumnChart data={data} />
        </div>

        {/* Summary cards */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-pt-4 tw-border-t tw-border-gray-200">
          <div className="tw-p-4 tw-rounded tw-bg-gradient-to-br tw-from-blue-50 tw-to-blue-100 tw-border tw-border-blue-200">
            <p className="tw-text-xs tw-text-blue-600 tw-font-semibold tw-uppercase tw-tracking-wide">
              Total CO#
            </p>
            <p className="tw-text-3xl tw-font-bold tw-text-blue-900 tw-mt-1">{totalCOs}</p>
            <p className="tw-text-xs tw-text-blue-600 tw-mt-1">Across {data.length} sales units</p>
          </div>
          <div className="tw-p-4 tw-rounded tw-bg-gradient-to-br tw-from-teal-50 tw-to-teal-100 tw-border tw-border-teal-200">
            <p className="tw-text-xs tw-text-teal-600 tw-font-semibold tw-uppercase tw-tracking-wide">
              Total Projects
            </p>
            <p className="tw-text-3xl tw-font-bold tw-text-teal-900 tw-mt-1">{totalProjects}</p>
            <p className="tw-text-xs tw-text-teal-600 tw-mt-1">Unique projects</p>
          </div>
          <div className="tw-p-4 tw-rounded tw-bg-gradient-to-br tw-from-amber-50 tw-to-amber-100 tw-border tw-border-amber-200">
            <p className="tw-text-xs tw-text-amber-600 tw-font-semibold tw-uppercase tw-tracking-wide">
              Top Sales Unit
            </p>
            <p className="tw-text-lg tw-font-bold tw-text-amber-900 tw-mt-1 tw-truncate">{topUnit}</p>
            <p className="tw-text-xs tw-text-amber-600 tw-mt-1">{data[0]?.total_cos || 0} change orders</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

SalesUnitChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      unit_id:        PropTypes.number,
      unit_name:      PropTypes.string,
      total_cos:      PropTypes.number,
      total_projects: PropTypes.number,
      p_count:        PropTypes.number,
      j_count:        PropTypes.number
    })
  ),
  loading: PropTypes.bool,
  error:   PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

SalesUnitChart.defaultProps = {
  data:    [],
  loading: false,
  error:   null
};

export default SalesUnitChart;
